import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/i18n";

export const scrollToHash = (hash) => (e) => {
  if (e) e.preventDefault();
  const el = document.querySelector(hash);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    ["#about", t.nav.about, "nav-about-link"],
    ["#vision", t.nav.vision, "nav-vision-link"],
    ["#services", t.nav.services, "nav-services-link"],
    ["#projects", t.nav.projects, "nav-projects-link"],
    ["#battery", t.nav.battery, "nav-battery-link"],
    ["#milestones", t.nav.milestones, "nav-milestones-link"],
    ["#contact", t.nav.contact, "nav-contact-link"],
  ];

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled ? "border-b border-brand-100 bg-white/85 shadow-[0_8px_30px_-18px_rgba(44,56,28,0.35)] backdrop-blur-xl" : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 lg:px-10">
        <a href="#top" onClick={scrollToHash("#top")} data-testid="nav-logo" className="flex items-center">
          <img src="/assets/electro-logo.png" alt="electro.technology" className="h-10 w-auto" />
        </a>

        <nav className="hidden items-center gap-7 lg:flex">
          {links.map(([hash, label, id]) => (
            <a
              key={hash}
              href={hash}
              data-testid={id}
              onClick={scrollToHash(hash)}
              className="text-[13px] font-medium text-neutral-500 transition-colors duration-300 hover:text-brand-700"
            >
              {label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="flex items-center rounded-full border border-neutral-200 bg-white p-1" data-testid="lang-toggle">
            {["en", "pl"].map((l) => (
              <button
                key={l}
                data-testid={`lang-toggle-${l}`}
                onClick={() => setLang(l)}
                className={`rounded-full px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-widest transition-colors duration-300 ${
                  lang === l ? "bg-brand-600 text-white" : "text-neutral-400 hover:text-neutral-700"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <a
            href="#contact"
            data-testid="nav-quote-cta"
            onClick={scrollToHash("#contact")}
            className="hidden rounded-full bg-neutral-900 px-5 py-2.5 text-[13px] font-semibold text-white transition-colors duration-300 hover:bg-brand-700 lg:inline-block"
          >
            {t.nav.cta}
          </a>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="grid h-10 w-10 place-items-center rounded-full border border-neutral-200 bg-white text-neutral-700 lg:hidden"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-neutral-100 bg-white/95 backdrop-blur-xl lg:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col px-6 py-4">
            {links.map(([hash, label, id]) => (
              <a
                key={hash}
                href={hash}
                data-testid={`${id}-mobile`}
                onClick={(e) => { scrollToHash(hash)(e); setOpen(false); }}
                className="border-b border-neutral-100 py-3.5 font-display text-lg font-bold text-neutral-800 last:border-0"
              >
                {label}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};
